import * as React from 'react';
import { connect } from 'react-redux';
import { actions } from 'react-redux-form';
import { find, remove } from 'lodash';
import { Button, Form, IconButton, Notification } from '../';
import MiniUserList from '../MiniUserList/MiniUserList';
import {
  addNote,
  updateNote,
  assignUser,
  unassignUser,
} from '../../redux/actions/notes';
import FIELDS from '../../data/fields';

interface Props {
  id: number;
  resource: string;
  note?: any;
  handleUpdate: Function;
  notes: any;
  users: any;
  dispatch: Function;
  hasDropdown: boolean;
}

interface State {
  note: any;
  isActive: boolean;
  fields: any;
  isResolvable: boolean;
  resetForm: Function;
  clearAssignee: Function;
  assignees: Array<any>;
  unassignees: Array<any>;
  error: any;
  formData: any;
  isSubmitting: boolean;
}

class NoteForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      note: this.props.note,
      isActive: !this.props.hasDropdown,
      fields: FIELDS.notes.normal,
      isResolvable: false,
      assignees: [],
      unassignees: [],
      error: '',
      formData: {},
      isSubmitting: false,
      resetForm: () => {},
      clearAssignee: () => {},
    };
    this.handleDispatch = this.handleDispatch.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleAssign = this.handleAssign.bind(this);
    this.handleUnassign = this.handleUnassign.bind(this);
  }

  /**
   * Populate assignees from note property
   */
  componentDidMount() {
    if (this.props.note && this.props.note.assignees) {
      this.setState({ assignees: this.props.note.assignees });
    }
  }

  /**
   * Handle form initialisation
   */
  handleDispatch(dispatch) {
    if (this.props.note) {
      dispatch(actions.change('notes.text', this.props.note.text || ''));
      dispatch(
        actions.change(
          'notes.resolveable',
          this.props.note.resolveable || false,
        ),
      );
    }
    this.setState({
      resetForm: () => {
        dispatch(actions.change('notes.text', ''));
        dispatch(actions.change('notes.resolvable', ''));
        dispatch(actions.change('notes.new_assignee', ''));
        this.setState({ assignees: [], isResolvable: false });
      },
      clearAssignee: () => {
        dispatch(actions.change('notes.new_assignee', ''));
      },
    });
  }

  /**
   * Add a user to the list of users to be assigned
   * @param {Object} user
   */
  handleAssign(user) {
    const assignees = this.state.assignees;
    const unassignees = this.state.unassignees;
    assignees.push(user);
    remove(unassignees, (entry) => user.id === entry.id);
    this.setState({ assignees, unassignees }, () => {
      this.state.clearAssignee();
    });
  }

  /**
   * Add a user to the list of users to be unassigned
   * @param {Object} user
   */
  handleUnassign(user) {
    const assignees = this.state.assignees;
    const unassignees = this.state.unassignees;
    unassignees.push(user);
    remove(assignees, (entry) => user.id === entry.id);
    this.setState({ assignees, unassignees }, () => {
      this.state.clearAssignee();
    });
  }

  /**
   * Handle input change
   * @param {Object} data
   */
  handleChange(data) {
    this.setState({ formData: data }, () => {
      // Determine whether to show assignee fields
      this.setState({
        isResolvable: data.resolveable,
        fields: data.resolveable
          ? FIELDS.notes.withAssignee
          : FIELDS.notes.normal,
      });

      // Determine whether to add a new assignee
      if (data.resolveable && data.new_assignee) {
        this.state.clearAssignee();
        const id = Number(data.new_assignee);
        if (!isNaN(id)) {
          const user = find(this.props.users, { id });
          const assignees = this.state.assignees;
          if (user && find(assignees, { id: id as any }) === undefined) {
            this.handleAssign(user);
          }
        }
      }
    });
  }

  /**
   * Handle adding/updating a note
   */
  async handleSubmit() {
    if (this.state.isSubmitting) {
      return;
    }

    this.setState({ isSubmitting: true });

    // Construct the payload
    const data = this.state.formData;
    const fields = {
      resource_id: this.props.id,
      resource_type: this.props.resource,
      text: data.text,
      resolveable: data.resolveable ? 1 : 0,
      assignees: JSON.stringify(this.state.assignees),
    };

    // Determine whether to add or update the note
    let response;
    if (this.props.note) {
      response = await this.props.dispatch(
        updateNote(this.props.note.id, fields),
      );
    } else {
      response = await this.props.dispatch(addNote(fields));
    }
    if (response.error) {
      this.setState({ error: response.error, isSubmitting: false });
      return;
    }

    // Assign new users and unassign old users
    if (data.resolveable) {
      for (const user of this.state.assignees) {
        try {
          await this.props.dispatch(assignUser(response.data.id, user.id));
        } catch (e) {}
      }
      for (const user of this.state.unassignees) {
        try {
          await this.props.dispatch(unassignUser(response.data.id, user.id));
        } catch (e) {}
      }
    }

    // Pass the new data back to the parent component and reset the form
    this.setState({ isActive: false, error: '', isSubmitting: false }, () => {
      this.state.resetForm();
      this.props.handleUpdate({
        ...response.data,
        assignees: this.state.assignees,
      });
    });
  }

  render() {
    if (this.state.isActive) {
      return (
        <>
          {this.state.error && (
            <Notification type="danger">{this.state.error}</Notification>
          )}
          <div className="field">
            <Form
              type="notes"
              fields={this.state.fields}
              getDispatch={this.handleDispatch}
              handleChange={this.handleChange}
              handleSubmit={this.handleSubmit}
              buttonText="Add Note"
              disabled={this.state.isSubmitting}
              values={{
                new_assignee: this.props.users
                  .filter((user) => user.active)
                  .map((user) => ({ name: user.name, value: user.id })),
              }}
              hasButton={false}
            />
          </div>
          {this.state.isResolvable && (
            <div className="field">
              <label>Assignees</label>
              <MiniUserList
                users={this.state.assignees}
                handleClick={this.handleUnassign}
              />
            </div>
          )}
          <div className="buttons is-right">
            {!this.props.note && (
              <IconButton
                icon="times"
                handleClick={() => this.setState({ isActive: false })}
                className="is-default"
              >
                Cancel
              </IconButton>
            )}
            <Button
              className={`is-primary ${
                this.state.isSubmitting ? 'is-loading' : ''
              }`}
              handleClick={this.handleSubmit}
            >
              {this.props.note !== undefined ? 'Save' : 'Create'}
            </Button>
          </div>
        </>
      );
    }

    if (this.props.hasDropdown) {
      return (
        <IconButton
          className="is-default is-fullwidth"
          icon="comment"
          handleClick={() => {
            this.setState({ isActive: !this.state.isActive });
          }}
        >
          New Note
        </IconButton>
      );
    }

    return null;
  }
}

const mapStateToProps = ({ notes, users }) => ({
  notes,
  users: users.users,
});

export default connect(mapStateToProps)(NoteForm);
